import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { CATEGORY_LABELS, tocProducts, tobProducts, type Product } from '../data/products';
import { IconChevronDown, IconClose, IconExternalLink, IconLayers, IconMenu } from './icons';
import { getProductIcon } from './icons/productIcons';

/** 参考站的浮层入场曲线：快速起步、末端收敛，没有回弹 */
const DROPDOWN_EASE = [0.16, 1, 0.3, 1] as const;

/**
 * 全站主导航。视觉采用 sx- 体系（近黑 + hairline + 胶囊 CTA），
 * 与首页落地页统一为同一个设计语言。
 *
 * 相比 dm- 金色玻璃版本的三处变化：
 * 1. 背景从彩色玻璃改为近黑半透明 —— 导航应当"几乎不存在"，
 *    只在滚动时通过模糊层与内容轻微分离。
 * 2. CTA 从金色圆角矩形改为白色胶囊，与全站"可交互即胶囊"的语义一致。
 * 3. 新增当前路由高亮。原版无法判断所处位置，这是可用性缺口而非风格问题。
 */
const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const { pathname } = useLocation();
  const reduce = useReducedMotion();

  const closeMobile = () => {
    setIsOpen(false);
    setMobileProductsOpen(false);
  };

  const isProductsActive = pathname.startsWith('/products');

  // 浮层展开时必须能用 Escape 收起，否则键盘用户只能靠 Tab 走完整个列表才能脱离
  useEffect(() => {
    if (!activeDropdown) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveDropdown(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [activeDropdown]);

  return (
    <nav className="sx-nav sticky top-0 z-50" aria-label="主导航">
      <div className="sx-shell py-3.5">
        <div className="flex justify-between items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2.5 no-underline focus-ring rounded-lg"
            onClick={closeMobile}
          >
            <span
              className="w-9 h-9 flex items-center justify-center shrink-0"
              style={{
                background: 'var(--sx-surface-2)',
                border: '1px solid var(--sx-line-strong)',
                borderRadius: 'var(--sx-r-inner)',
              }}
              aria-hidden="true"
            >
              <span className="text-white font-semibold text-sm">三</span>
            </span>
            <span
              className="text-base md:text-lg font-medium text-white"
              style={{ letterSpacing: '-0.02em' }}
            >
              三米云科技
            </span>
            <span className="sx-mono hidden md:inline ml-1">Sanmiyun</span>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            <NavItem to="/" active={pathname === '/'}>
              首页
            </NavItem>

            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('products')}
              onMouseLeave={() => setActiveDropdown(null)}
              onBlur={(event) => {
                // 焦点移出整块区域时收起，让键盘 Tab 的行为和鼠标移出一致
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setActiveDropdown(null);
                }
              }}
            >
              <button
                type="button"
                className="sx-nav-trigger"
                style={isProductsActive ? { color: 'var(--sx-text)' } : undefined}
                aria-expanded={activeDropdown === 'products'}
                aria-haspopup="true"
                onClick={() =>
                  setActiveDropdown((prev) => (prev === 'products' ? null : 'products'))
                }
              >
                产品
                <IconChevronDown
                  size={15}
                  className={`transition-transform duration-300 ${
                    activeDropdown === 'products' ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/*
                浮层改用 AnimatePresence：原实现是条件渲染，收起时直接从 DOM 消失，
                视觉上是"闪断"。这里给 180ms 的淡入淡出 + 6px 位移，
                曲线末端收敛、无回弹，与参考站浮层的节奏一致。
              */}
              <AnimatePresence>
                {activeDropdown === 'products' && (
                  <motion.div
                    className="absolute top-full left-0 pt-2.5 w-64 z-50"
                    initial={{ opacity: 0, y: reduce ? 0 : -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: reduce ? 0 : -6 }}
                    transition={{ duration: reduce ? 0.01 : 0.18, ease: DROPDOWN_EASE }}
                  >
                    <div className="sx-dropdown py-1.5">
                      <Link
                        to="/products"
                        className="sx-dropdown-item"
                        style={{ color: 'var(--sx-text)' }}
                        onClick={() => setActiveDropdown(null)}
                      >
                        <IconLayers size={16} className="shrink-0" />
                        产品总览
                      </Link>

                      <p className="sx-mono px-3.5 pt-3 pb-1.5">{CATEGORY_LABELS.toc}</p>
                      {tocProducts.map((product) => (
                        <DropdownProductItem
                          key={product.id}
                          product={product}
                          fallbackHash="#toc"
                          onNavigate={() => setActiveDropdown(null)}
                        />
                      ))}

                      <p className="sx-mono px-3.5 pt-3 pb-1.5">{CATEGORY_LABELS.tob}</p>
                      {tobProducts.map((product) => (
                        <DropdownProductItem
                          key={product.id}
                          product={product}
                          fallbackHash="#tob"
                          onNavigate={() => setActiveDropdown(null)}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavItem to="/about" active={pathname === '/about'}>
              关于
            </NavItem>
            <NavItem to="/news" active={pathname === '/news'}>
              动态
            </NavItem>
            {/*
              AI 助手是挂在 Layout 之外的独立全屏应用（见 routes/index.tsx），
              进去之后没有官网的导航和页脚。在新标签页打开，用户关掉即回到原来
              的浏览位置，不会把官网动线冲掉。用 <a> 而非 <Link> 是必须的：
              react-router 的客户端跳转不响应 target。
            */}
            <a
              href="/ai"
              target="_blank"
              rel="noopener noreferrer"
              className="sx-nav-link inline-flex items-center gap-1.5"
            >
              AI 助手
              <IconExternalLink size={13} style={{ opacity: 0.5 }} />
              <span className="sr-only">（在新标签页打开）</span>
            </a>
            <NavItem to="/contact" active={pathname === '/contact'}>
              联系
            </NavItem>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/contact" className="hidden sm:inline-flex sx-pill sx-pill-primary sx-pill-sm">
              商务合作
            </Link>

            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden min-w-[44px] min-h-[44px] inline-flex items-center justify-center focus-ring rounded-lg"
              style={{ color: 'var(--sx-dim)' }}
              aria-label={isOpen ? '关闭菜单' : '打开菜单'}
              aria-expanded={isOpen}
              aria-controls="mobile-nav"
            >
              {isOpen ? <IconClose size={21} /> : <IconMenu size={21} />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div
            id="mobile-nav"
            className="lg:hidden mt-4 pb-2 pt-4 space-y-0.5"
            style={{ borderTop: '1px solid var(--sx-line)' }}
          >
            <MobileNavLink to="/" onClick={closeMobile}>
              首页
            </MobileNavLink>

            <button
              type="button"
              className="w-full flex items-center justify-between py-3 focus-ring rounded-lg text-sm"
              style={{ color: 'var(--sx-text)', background: 'none', border: 'none' }}
              onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
              aria-expanded={mobileProductsOpen}
            >
              产品
              <IconChevronDown
                size={15}
                className={`transition-transform duration-300 ${
                  mobileProductsOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {mobileProductsOpen && (
              <div
                className="pl-3 ml-2 mb-2 space-y-0.5"
                style={{ borderLeft: '1px solid var(--sx-line)' }}
              >
                <MobileNavLink to="/products" onClick={closeMobile}>
                  产品总览
                </MobileNavLink>
                {[...tocProducts, ...tobProducts].map((product) => (
                  <MobileNavLink
                    key={product.id}
                    to={product.route ?? '/products#toc'}
                    onClick={closeMobile}
                  >
                    {product.name}
                  </MobileNavLink>
                ))}
              </div>
            )}

            <MobileNavLink to="/about" onClick={closeMobile}>
              关于
            </MobileNavLink>
            <MobileNavLink to="/news" onClick={closeMobile}>
              动态
            </MobileNavLink>
            <a
              href="/ai"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobile}
              className="flex items-center gap-1.5 py-3 px-1 min-h-[44px] text-sm focus-ring rounded-lg no-underline"
              style={{ color: 'var(--sx-dim)' }}
            >
              AI 助手
              <IconExternalLink size={13} style={{ opacity: 0.5 }} />
              <span className="sr-only">（在新标签页打开）</span>
            </a>
            <MobileNavLink to="/contact" onClick={closeMobile}>
              联系
            </MobileNavLink>

            <Link
              to="/contact"
              onClick={closeMobile}
              className="sx-pill sx-pill-primary w-full mt-4"
            >
              商务合作
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

/**
 * 下拉里的产品项。带图标是为了让五个产品在同一列表里能靠形状区分，
 * 而不是只能靠读四个汉字 —— 图标 opacity 由 CSS 在 hover 时提亮。
 */
const DropdownProductItem: React.FC<{
  product: Product;
  fallbackHash: string;
  onNavigate: () => void;
}> = ({ product, fallbackHash, onNavigate }) => {
  const Icon = getProductIcon(product.id);
  return (
    <Link
      to={product.route ?? `/products${fallbackHash}`}
      className="sx-dropdown-item"
      onClick={onNavigate}
    >
      {Icon && <Icon size={16} className="shrink-0" />}
      <span className="flex-1 truncate">{product.name}</span>
    </Link>
  );
};

const NavItem: React.FC<{ to: string; active: boolean; children: React.ReactNode }> = ({
  to,
  active,
  children,
}) => (
  <Link
    to={to}
    className="sx-nav-link"
    style={active ? { color: 'var(--sx-text)' } : undefined}
    aria-current={active ? 'page' : undefined}
  >
    {children}
  </Link>
);

const MobileNavLink: React.FC<{ to: string; children: React.ReactNode; onClick?: () => void }> = ({
  to,
  children,
  onClick,
}) => (
  <Link
    to={to}
    className="flex items-center py-3 px-1 min-h-[44px] text-sm focus-ring rounded-lg no-underline"
    style={{ color: 'var(--sx-dim)' }}
    onClick={onClick}
  >
    {children}
  </Link>
);

export default Navbar;
